import React from 'react';
import {
  Alert,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle, Info, RadioButtonUnchecked } from '@mui/icons-material';

import { IGwGPflichten } from '../../../Interfaces/IGwGPruefung';

interface IProps {
  pflichten: IGwGPflichten;
  /** Ohne eine einzige Antwort ist die Liste leer — und das heißt nichts. */
  hatAntworten: boolean;
}

/**
 * Was nach der Prüfung noch zu tun ist — und was davon schon vorliegt.
 *
 * Die Liste beantwortet „haben wir alles?“. Sie tut das nur dann ehrlich,
 * wenn sie drei Lagen unterscheidet: die Unterlage liegt im GwG-Ordner, sie
 * fehlt bei bestimmten Beteiligten, oder sie lässt sich dort gar nicht
 * nachweisen. Der dritte Fall darf nicht wie der erste aussehen.
 */
export const GwGPflichtenListe: React.FC<IProps> = ({
  pflichten,
  hatAntworten,
}) => {
  if (!hatAntworten) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Die Pflichten ergeben sich aus den Antworten. Beginnen Sie die Prüfung,
        um zu sehen, was zu tun ist.
      </Typography>
    );
  }

  if (pflichten.PflichtArray.length === 0) {
    return (
      <Alert severity='success' variant='outlined'>
        Aus den bisherigen Antworten ergeben sich keine Pflichten.
      </Alert>
    );
  }

  return (
    <Box>
      <Alert
        severity={pflichten.istVollstaendig ? 'success' : 'warning'}
        variant='outlined'
        sx={{ mb: 1 }}
      >
        {pflichten.istVollstaendig
          ? 'Alle Pflichten aus dieser Prüfung sind erfüllt.'
          : `${pflichten.Offen} von ${pflichten.PflichtArray.length} Pflichten sind noch offen.`}
      </Alert>

      <List dense disablePadding>
        {pflichten.PflichtArray.map(pflicht => (
          <ListItem
            key={pflicht.Titel}
            alignItems='flex-start'
            sx={{ px: 0, alignItems: 'flex-start' }}
          >
            <ListItemIcon sx={{ minWidth: 34, mt: 0.5 }}>
              {pflicht.istErfuellt ? (
                <CheckCircle color='success' fontSize='small' />
              ) : (
                <RadioButtonUnchecked color='warning' fontSize='small' />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='center'
                  sx={{ flexWrap: 'wrap', gap: 0.5 }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {pflicht.Titel}
                  </Typography>
                  <Chip
                    size='small'
                    variant='outlined'
                    label={pflicht.Quelle}
                  />
                  {pflicht.istVerpflichtend === false && (
                    <Chip size='small' variant='outlined' label='empfohlen' />
                  )}
                  {pflicht.istVerpflichtend === true && (
                    <Chip
                      size='small'
                      color='error'
                      variant='outlined'
                      label='Beurkundungsverbot'
                    />
                  )}
                </Stack>
              }
              secondary={
                <>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                  >
                    {pflicht.Begruendung}
                  </Typography>

                  {pflicht.OffenArray.length > 0 && (
                    <Typography
                      variant='caption'
                      color='warning.main'
                      display='block'
                    >
                      Fehlt bei:{' '}
                      {pflicht.OffenArray.map(offen => offen.Name).join(', ')}
                    </Typography>
                  )}

                  {pflicht.Hinweis && (
                    <Stack
                      direction='row'
                      spacing={0.5}
                      alignItems='flex-start'
                      sx={{ mt: 0.3 }}
                    >
                      <Info sx={{ fontSize: 14, mt: 0.2 }} color='action' />
                      <Typography variant='caption' color='text.secondary'>
                        {pflicht.Hinweis}
                      </Typography>
                    </Stack>
                  )}
                </>
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};
